/**
 * R-131-EPISTEMIC-GROUNDING-C — single finding row with evidence-state chrome.
 *
 * Editorial conversion (findings-bop group): the saturated red/amber/blue
 * borders + filled chips are replaced with a theme-aware accent LEFT-rule
 * (severity-toned via roleColor) and an EditorialBadge evidence tag —
 * verified citations read as a POSITIVE small-caps check, pending as
 * WARNING, open questions as WARNING "needs operator confirmation". The
 * open-question text is DEDUPED (the prior bug rendered the claim and the
 * identical `openQuestion` body twice). All chrome reads roleColor(theme,…)
 * so it follows the active preset; with no `theme` it falls through to
 * DEFAULT_THEME, keeping legacy (no-preset) reports visually consistent.
 */
import React, { useState } from 'react';
import type { DataSource } from '../../../types/siteRag';
import type {
  FindingClaim,
  OpenQuestionAnswerValue,
  OpenQuestionResponse,
} from '../../../lib/findingClaim';
import {
  isOperatorAttestedClaim,
  OPEN_QUESTION_DETAILS_MAX_CHARS,
  resolveOpenQuestionIdentity,
} from '../../../lib/findingClaim';
import { walkCitationText } from '../../../lib/walkCitationText';
import type { ReportTheme } from '../../../types/reportTheme';
import { useEffectiveReportTheme } from '../../../lib/useEffectiveReportTheme';
import { roleColor, roleColorAlpha } from '../../../lib/reportRoleColors';
import {
  reportTypographySizes,
  REPORT_LINE_HEIGHT,
} from '../../../lib/reportSectionHeading';
import { RefLink } from './RefLink';
import { EditorialBadge, type EditorialBadgeTone, type EditorialBadgeVariant } from './editorial';
import { isEditorialLayoutsEnabled } from '../../../lib/runtimeFlags';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';

export interface FindingClaimCardProps {
  claim: FindingClaim;
  references?: DataSource[];
  /** When true, show inline Answer affordance for open questions. */
  showAnswerAffordance?: boolean;
  onAnswer?: (
    answer: OpenQuestionAnswerValue,
    details?: string,
  ) => void | boolean | Promise<void | boolean>;
  answering?: boolean;
  /** Previously recorded operator response. The original question remains visible. */
  response?: OpenQuestionResponse;
  /** Stable marker used to relay clicks from the detached Paged.js clone. */
  pagedControlId?: string;
  /**
   * Active report theme — drives the editorial chrome (left-rule tone,
   * evidence-badge ink). Optional: omitting it falls through to
   * DEFAULT_THEME so existing call sites (which pass no theme) keep a
   * visually consistent cool-slate / cyan-accent look.
   */
  theme?: ReportTheme;
}

function evidenceChipLabel(claim: FindingClaim, references?: DataSource[]): string {
  if (claim.evidenceState === 'unknown') {
    return 'Open question: needs operator confirmation';
  }
  if (claim.evidenceState === 'likely_present_unverified') {
    return 'Documentation suggests this; verification pending';
  }
  if (isOperatorAttestedClaim(claim)) {
    return claim.evidence?.excerpt?.trim() ?? 'Operator attested';
  }
  const refId = claim.evidence?.refIds?.[0];
  if (refId != null && Number.isFinite(refId)) {
    return `Documentation confirms this – [Ref ${refId}]`;
  }
  return 'Documentation confirms this';
}

// William QA 2026-06-28 — left accent rule removed report-wide (STATE_RULE_ROLE dropped)

const STATE_BADGE: Record<
  FindingClaim['evidenceState'],
  { tone: EditorialBadgeTone; variant: EditorialBadgeVariant }
> = {
  confirmed: { tone: 'positive', variant: 'check' },
  likely_present_unverified: { tone: 'warning', variant: 'text' },
  unknown: { tone: 'warning', variant: 'text' },
};

export function FindingClaimCard({
  claim,
  references = [],
  showAnswerAffordance = false,
  onAnswer,
  answering = false,
  response,
  pagedControlId,
  theme: themeProp,
}: FindingClaimCardProps) {
  // No explicit theme ⇒ resolve the EFFECTIVE theme (preset + reader-mode
  // stamp) instead of bare DEFAULT_THEME. An unstamped default resolved
  // light-mode ink, which rendered invisible claim text when a host section
  // forgot to thread `theme` in the dark reader (William QA 2026-08-17).
  const effectiveTheme = useEffectiveReportTheme();
  const theme = themeProp ?? effectiveTheme;
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsText, setDetailsText] = useState(response?.details ?? '');
  const resolvedQuestion = resolveOpenQuestionIdentity(claim).question;

  const hasRecordedResponse = claim.evidenceState === 'unknown' && Boolean(response);
  const badge = hasRecordedResponse
    ? { tone: 'neutral' as const, variant: 'text' as const }
    : STATE_BADGE[claim.evidenceState];
  // N1.5C — the finding-row density match is EDITORIAL-MODE ONLY so the
  // flag-OFF reportSurfaceParity baseline stays byte-stable (the constraint:
  // never re-baseline flag-OFF). Read the build-time flag directly (the same
  // gate the report views use) — this component is mounted deep in several
  // call sites that do not thread `editorialActive`.
  const editorialDensity = isEditorialLayoutsEnabled();
  // PART K1 — the finding prose + open-question line route their SIZE through
  // the shared report type scale (body / eyebrow tiers) instead of the bare
  // `text-sm` / `text-xs` literals, so a finding card tracks the active
  // theme's density alongside every other report block. Faces/colours
  // unchanged (roleColor still owns the ink).
  const sizes = reportTypographySizes(theme);

  const chipLabel = hasRecordedResponse
    ? 'Operator response recorded'
    : evidenceChipLabel(claim, references);
  const refMatch = /\[Ref\s+(\d+)\]/i.exec(chipLabel);
  const refN = refMatch ? Number(refMatch[1]) : null;

  // DEDUP: only render the separate openQuestion line when it adds text
  // beyond the claim itself (the prior bug rendered both even when equal).
  const openQuestionText = claim.openQuestion?.trim();
  const showOpenQuestionLine =
    claim.evidenceState === 'unknown'
    && !!openQuestionText
    && openQuestionText !== claim.claim.trim();

  const openDetails = () => {
    setDetailsText(response?.details ?? '');
    setDetailsOpen(true);
  };

  const handleDetailsOpenChange = (open: boolean) => {
    if (open) setDetailsText(response?.details ?? '');
    setDetailsOpen(open);
  };

  const saveDetails = async () => {
    const details = detailsText.trim();
    if (!details || !onAnswer) return;
    // Details are notes attached to the current disposition. Editing them must
    // not turn a recorded Yes/No into the legacy details-only state.
    const answer = response?.answer === 'yes' || response?.answer === 'no'
      ? response.answer
      : 'details';
    const saved = await onAnswer(answer, details);
    if (saved !== false) setDetailsOpen(false);
  };

  const saveDisposition = (answer: 'yes' | 'no') => {
    const existingDetails = response?.details?.trim();
    return existingDetails ? onAnswer?.(answer, existingDetails) : onAnswer?.(answer);
  };

  const responseLabel = response?.answer === 'yes'
    ? 'Yes'
    : response?.answer === 'no'
      ? 'No'
      : response
        ? 'Details recorded'
        : '';
  const responseDate = response?.answeredAt?.slice(0, 10);

  return (
    <article
      // N1.5C density match (EDITORIAL-MODE ONLY) — when the editorial flag is ON,
      // drop the residual `rounded-md` card-rounding (a no-op on a transparent
      // fill, but it signalled card chrome) + the 3px left gap so the finding row
      // reads as a flat editorial entry behind a single evidence-toned LEFT-rule
      // (matching the ContextualKeyFindings left-rule rhythm William
      // screenshotted). Flag OFF keeps the prior `rounded-md px-3 py-2` + 4px rule
      // so the flag-OFF reportSurfaceParity baseline stays byte-stable.
      className={editorialDensity ? 'py-1.5' : 'rounded-md px-3 py-2'}
      data-testid={`finding-claim-${claim.evidenceState}`}
      data-evidence-state={claim.evidenceState}
      style={{
        // William QA 2026-06-28 — left accent rule removed report-wide; the
        // finding / open-question / claim row reads as flush-left ink-on-cream,
        // its evidence state carried by the badge chip below (not a left rule).
        background: 'transparent',
      }}
    >
      <p
        className="leading-relaxed"
        style={{ color: roleColor(theme, 'ink'), fontSize: sizes.body, lineHeight: REPORT_LINE_HEIGHT.body }}
      >
        {walkCitationText(claim.claim, references)}
      </p>
      {showOpenQuestionLine && (
        <p className="mt-1" style={{ color: roleColor(theme, 'muted'), fontSize: sizes.eyebrow }}>
          {openQuestionText}
        </p>
      )}
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <EditorialBadge
          theme={theme}
          tone={badge.tone}
          variant={badge.variant}
          title={chipLabel}
        >
          <span data-testid="finding-claim-evidence-chip">
            {claim.evidenceState === 'confirmed' && !isOperatorAttestedClaim(claim) && refN != null ? (
              <>
                Documentation confirms this –{' '}
                <RefLink n={refN} reference={references[refN - 1]} />
              </>
            ) : (
              chipLabel
            )}
          </span>
        </EditorialBadge>
      </div>
      {response && (
        <div
          className="mt-2 flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t pt-2"
          data-testid="finding-claim-recorded-response"
          data-open-question-response={response.answer}
          style={{ borderColor: roleColor(theme, 'rule') }}
        >
          <span
            className="font-semibold uppercase tracking-[0.12em]"
            style={{ color: roleColor(theme, 'accent'), fontSize: sizes.eyebrow }}
          >
            Response
          </span>
          <span style={{ color: roleColor(theme, 'body'), fontSize: sizes.eyebrow }}>
            {responseLabel}
          </span>
          {responseDate && (
            <span style={{ color: roleColor(theme, 'muted'), fontSize: sizes.eyebrow }}>
              · {responseDate}
            </span>
          )}
          {response.sedSyncStatus === 'pending' && (
            <span
              data-testid="finding-claim-sed-sync-pending"
              style={{ color: roleColor(theme, 'warning'), fontSize: sizes.eyebrow }}
            >
              · SED sync pending
            </span>
          )}
          {response.details?.trim() && (
            <p
              className="basis-full leading-relaxed"
              data-testid="finding-claim-recorded-details"
              style={{ color: roleColor(theme, 'muted'), fontSize: sizes.eyebrow }}
            >
              {response.details.trim()}
            </p>
          )}
        </div>
      )}
      {showAnswerAffordance && claim.evidenceState === 'unknown' && onAnswer && (
        <div className="mt-3 space-y-2" data-testid="finding-claim-answer-affordance">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className="rounded border px-2 py-1 text-xs font-medium hover:bg-surface-2"
              style={{ borderColor: roleColor(theme, 'rule'), color: roleColor(theme, 'body') }}
              disabled={answering}
              aria-pressed={response?.answer === 'yes'}
              data-paged-control-id={pagedControlId}
              data-paged-control-action="yes"
              onClick={() => void saveDisposition('yes')}
            >
              Yes
            </button>
            <button
              type="button"
              className="rounded border px-2 py-1 text-xs font-medium"
              style={{
                borderColor: roleColorAlpha(theme, 'negative', 0.42),
                color: roleColor(theme, 'negative'),
              }}
              disabled={answering}
              aria-pressed={response?.answer === 'no'}
              data-testid="finding-claim-answer-no"
              data-paged-control-id={pagedControlId}
              data-paged-control-action="no"
              onClick={() => void saveDisposition('no')}
            >
              No, confirm absence
            </button>
            <button
              type="button"
              className="rounded border px-2 py-1 text-xs font-medium hover:bg-surface-2"
              style={{ borderColor: roleColor(theme, 'rule'), color: roleColor(theme, 'body') }}
              disabled={answering}
              data-paged-control-id={pagedControlId}
              data-paged-control-action="details"
              onClick={openDetails}
            >
              {response?.details?.trim() ? 'Edit details' : 'Details'}
            </button>
          </div>
          <Dialog open={detailsOpen} onOpenChange={handleDetailsOpenChange}>
            <DialogContent size="sm" className="gap-0 overflow-hidden p-0">
              <DialogHeader className="border-b border-border px-5 py-4 pr-12">
                <DialogTitle>Record operator details</DialogTitle>
                <DialogDescription className="leading-relaxed">
                  {resolvedQuestion}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-2 px-5 py-4">
                <label
                  htmlFor={`${pagedControlId ?? 'open-question'}-details`}
                  className="text-xs font-semibold uppercase tracking-wider text-text-secondary"
                >
                  Details
                </label>
                <textarea
                  id={`${pagedControlId ?? 'open-question'}-details`}
                  className="min-h-[112px] w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm leading-relaxed text-text-primary outline-none focus:border-primary focus:ring-2 focus:ring-primary/20"
                  placeholder="Add the operator's source, constraints, dates, or follow-up notes…"
                  value={detailsText}
                  onChange={(event) => setDetailsText(event.target.value)}
                  maxLength={OPEN_QUESTION_DETAILS_MAX_CHARS}
                  autoFocus
                />
                <p className="text-xs leading-relaxed text-text-muted">
                  This response is saved with the report and the site evidence record for future reports.
                </p>
              </div>
              <DialogFooter>
                <button
                  type="button"
                  className="rounded border border-border px-3 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-2"
                  disabled={answering}
                  onClick={() => setDetailsOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="rounded bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50"
                  disabled={answering || !detailsText.trim()}
                  onClick={() => void saveDetails()}
                >
                  Save details
                </button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}
    </article>
  );
}
